import React from "react";
import {
  Button,
  Drawer,
  Heading,
  Pager,
  Pill,
  Table,
  Text,
  CardNotification,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useLayoutStore } from "@context/useLayoutStore";
import { useInstructorTeachings } from "@services/collections/instructor/instructorServices";
import { getDifferenceState, formatDifference } from "@utils/teachings";
import {
  InstructorTeachingRow,
  TeachingDifferenceState,
} from "@services/types/instructor/instructorTypes";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import styles from "./instructorTeachingsDrawer.module.scss";

const DEFAULT_PAGE_SIZE = 10;

interface InstructorTeachingsDrawerProps {
  instructorId: string;
  scenario: string;
  origin: string;
  isOpen: boolean;
  onClose: () => void;
}

const differenceBadgeConfig: {
  [state in TeachingDifferenceState]: {
    icon: "circle-check" | "alert-triangle" | "circle-cross";
    color: "success-10" | "warning-10" | "error-10";
    textColor: "success-60" | "warning-60" | "error-60";
    iconColor: "success-50" | "warning-50" | "error-50";
  };
} = {
  success: {
    icon: "circle-check",
    color: "success-10",
    textColor: "success-60",
    iconColor: "success-50",
  },
  warning: {
    icon: "alert-triangle",
    color: "warning-10",
    textColor: "warning-60",
    iconColor: "warning-50",
  },
  error: { icon: "circle-cross", color: "error-10", textColor: "error-60", iconColor: "error-50" },
};

const InstructorTeachingsDrawer: React.FC<InstructorTeachingsDrawerProps> = ({
  instructorId,
  scenario,
  origin,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const { teachings, isLoading, hasError, pageInfo, onPageChange } = useInstructorTeachings({
    instructorId,
    scenarioId: scenario,
    originId: origin,
    shouldFetch: isOpen,
  });

  const renderCourse = (data: unknown) => {
    const row = data as InstructorTeachingRow;

    return (
      <div className={styles.instructorTeachingsDrawer_course}>
        <Text className={styles.instructorTeachingsDrawer_courseText}>{row.courseCode}</Text>
        <Text className={styles.instructorTeachingsDrawer_courseText}>{row.courseName}</Text>
      </div>
    );
  };

  const renderComponent = (data: unknown) => {
    const row = data as InstructorTeachingRow;

    return (
      <Pill color="gray-10" textColor="neutral-80">
        {row.componentName}
      </Pill>
    );
  };

  const renderBlocks = (value: number | null) => (
    <Text className={styles.instructorTeachingsDrawer_blocksText}>{value ?? "—"}</Text>
  );

  const renderDifference = (data: unknown) => {
    const row = data as InstructorTeachingRow;
    const state = getDifferenceState(row);

    if (!state) {
      return renderBlocks(null);
    }

    const badge = differenceBadgeConfig[state];

    return (
      <Pill
        className={styles.instructorTeachingsDrawer_differencePill}
        icon={badge.icon}
        iconColor={badge.iconColor}
        iconFilled
        color={badge.color}
        textColor={badge.textColor}
      >
        {formatDifference(row.difference)}
      </Pill>
    );
  };

  const columns = [
    {
      key: "course",
      header: t("dimension.instructor.teachings-drawer.columns.course"),
      renderCell: renderCourse,
    },
    {
      key: "component",
      header: t("dimension.instructor.teachings-drawer.columns.component"),
      renderCell: renderComponent,
    },
    {
      key: "contracted",
      header: t("dimension.instructor.teachings-drawer.columns.contracted"),
      renderCell: (data: unknown) => renderBlocks((data as InstructorTeachingRow).contractedBlocks),
    },
    {
      key: "planned",
      header: t("dimension.instructor.teachings-drawer.columns.planned"),
      renderCell: (data: unknown) => renderBlocks((data as InstructorTeachingRow).plannedBlocks),
    },
    {
      key: "difference",
      header: t("dimension.instructor.teachings-drawer.columns.difference"),
      renderCell: renderDifference,
    },
  ];

  return (
    <Drawer
      className={styles.instructorTeachingsDrawer}
      title={<Heading type="h3">{t("dimension.instructor.teachings-drawer.title")}</Heading>}
      isOpen={isOpen}
      onClose={onClose}
      heightGap={visibleHeaderPixels}
      closeOnOverlayClick
      footer={
        <div className={styles.instructorTeachingsDrawer_footer}>
          <Button variant="ghost" onClick={onClose}>
            {t("generals.close")}
          </Button>
        </div>
      }
    >
      <div className={styles.instructorTeachingsDrawer_content}>
        {isLoading && (
          <DimensionsSearchSkeleton rows={5} showHeader={false} showPagination={false} />
        )}

        {!isLoading && hasError && (
          <CardNotification
            title={t("dimension.instructor.teachings-drawer.error.title")}
            state="error"
          >
            {t("dimension.instructor.teachings-drawer.error.description")}
          </CardNotification>
        )}

        {!isLoading && !hasError && !teachings.length && (
          <CardNotification title={t("dimension.instructor.teachings-drawer.empty")} state="info" />
        )}

        {!isLoading && !hasError && !!teachings.length && (
          <>
            <Table data={teachings} columns={columns} />

            {!!pageInfo?.page && (
              <Pager
                onChange={onPageChange}
                total={pageInfo?.total ?? 0}
                page={pageInfo?.page ?? 1}
                size={DEFAULT_PAGE_SIZE}
                classname={{ global: styles.instructorTeachingsDrawer_pager }}
              />
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default InstructorTeachingsDrawer;
